import type { ReactNode } from 'react'

import { Button } from '@/components/ui/button'
import { artifactCanvasSectionPaddingClass } from '@/lib/artifact-canvas-section-padding'
import { cn } from '@/lib/utils'

interface ArtifactCanvasLayoutProps {
  children: ReactNode
  className?: string
}

/**
 * Standard wrapper for content rendered inside `EditableArtifactCanvasShell`
 * (`openCanvas({ content })`). Provides default **24px** section inset (`p-6`) and vertical
 * spacing between **direct children** (`space-y-6`).
 *
 * **When to use:** Wrap the root of canvas payloads (domain `*Canvas` components and
 * multi-step flows) so padding matches across artifacts. Pass `className` to override
 * defaults—e.g. `flex h-full min-h-0 flex-col space-y-0 p-0` when the canvas stacks a
 * scrollable block (form) and a bottom action strip; give the main block
 * `className="min-h-0 flex-1 space-y-6 overflow-y-auto p-6"`.
 *
 * **Scroll:** The shell uses a flex column (`overflow-hidden`); canvases that need scroll
 * must put `overflow-y-auto` on the main block (`min-h-0 flex-1`). Prefer `h-full min-h-0`
 * on the body over `min-h-full` alone (the latter grows with content and never scrolls).
 * Footers with **`ArtifactCanvasFooter`** pin to the pane bottom when the body is
 * `flex h-full min-h-0 flex-col` and the footer is a direct sibling of the scroll region.
 *
 * @see ui/src/components/artifact/editable-artifact-canvas-shell.tsx
 */
export function ArtifactCanvasBody({ children, className }: ArtifactCanvasLayoutProps) {
  return (
    <div className={cn('space-y-6', artifactCanvasSectionPaddingClass, className)}>
      {children}
    </div>
  )
}

/**
 * Bottom action strip for artifact content (optional). Pinned to the pane bottom when the
 * canvas body uses `flex h-full min-h-0 flex-col` and the footer is a direct sibling of
 * the scrollable/flex-1 main block.
 */
export function ArtifactCanvasFooter({ children, className }: ArtifactCanvasLayoutProps) {
  return (
    <div className={cn('mt-auto shrink-0 bg-background', artifactCanvasSectionPaddingClass, className)}>
      {children}
    </div>
  )
}

/** Validation / server error strip below scrollable canvas content. */
export function ArtifactCanvasInlineError({
  children,
  className,
}: {
  children?: ReactNode
  className?: string
}) {
  if (!children) return null

  return (
    <div
      role="alert"
      className={cn('shrink-0 bg-destructive/10 px-6 py-2 text-sm text-destructive', className)}
    >
      {children}
    </div>
  )
}

/**
 * Top **Act** zone for artifact canvases — wrapping button/control row.
 * Employer applicant panel and candidate application status share this shell;
 * action *contents* stay per-surface (do not merge behavioural bundles).
 */
export function ArtifactCanvasActionRow({
  children,
  className,
  'aria-label': ariaLabel = 'Actions',
}: {
  children: ReactNode
  className?: string
  'aria-label'?: string
}) {
  if (children == null || children === false) return null

  return (
    <div
      className={cn('flex flex-wrap items-center gap-2', className)}
      role="group"
      aria-label={ariaLabel}
    >
      {children}
    </div>
  )
}

/** Footer row: optional leading slot (char count, destructive action) + trailing action cluster. */
export function ArtifactCanvasFooterBar({
  leading,
  actions,
  className,
  footerClassName,
  actionsClassName,
}: {
  leading?: ReactNode
  actions: ReactNode
  className?: string
  footerClassName?: string
  actionsClassName?: string
}) {
  return (
    <ArtifactCanvasFooter className={footerClassName}>
      <div
        className={cn(
          'flex items-center gap-4',
          leading ? 'justify-between' : 'justify-end',
          className,
        )}
      >
        {leading}
        <div className={cn('flex items-center gap-2', actionsClassName)}>{actions}</div>
      </div>
    </ArtifactCanvasFooter>
  )
}

/** Standard Cancel + Save cluster for batch-save artifact canvases. */
export function ArtifactCanvasSaveActions({
  onClose,
  onSave,
  isDirty,
  isSaving,
  saveLabel = 'Save',
  savingLabel = 'Saving…',
  closeLabel = 'Cancel',
  closeDisabled,
  saveDisabled,
}: {
  onClose: () => void
  onSave: () => void
  isDirty: boolean
  isSaving: boolean
  saveLabel?: string
  savingLabel?: string
  closeLabel?: string
  closeDisabled?: boolean
  /** When set, overrides the default `!isDirty` disable rule. */
  saveDisabled?: boolean
}) {
  const saveIsDisabled = (saveDisabled ?? !isDirty) || isSaving
  return (
    <>
      <Button type="button" variant="outline" onClick={onClose} disabled={closeDisabled}>
        {closeLabel}
      </Button>
      <Button type="button" onClick={onSave} disabled={saveIsDisabled}>
        {isSaving ? savingLabel : saveLabel}
      </Button>
    </>
  )
}

/** Dismiss-only footer for canvases that persist on each edit (e.g. profile skills manager). */
export function ArtifactCanvasDismissAction({
  onClose,
  label = 'Done',
  disabled,
}: {
  onClose: () => void
  label?: string
  disabled?: boolean
}) {
  return (
    <Button type="button" variant="outline" size="sm" onClick={onClose} disabled={disabled}>
      {label}
    </Button>
  )
}
